import { useQueries, useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import React, { memo, useCallback } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Dispatch } from 'redux';
import { RootState } from '../../../../../../../@types/RootState';
import { FilterButton, InnerFilter, Layout } from '../../../../../../../components';
import * as service from '../../../../../../../components/FilterSection/services';
import { SegmentsPlaceholder } from '../../../../../../../components/NewFilterBox/Components';
import { useDebounce } from '../../../../../../../hooks';
import { selectorSegmentos } from '../../../../../../../reducers/gerenciadorPrecos/segmentos';
import {
  RESET_RULES_PAGE_FILTERS_CACHE,
  selectorRulesPageFiltersCache,
  SET_RULES_PAGE_FILTERS_CACHE_BY_KEY,
} from '../../../../../../../reducers/ipa/strategy/rulesPage/filters/cache';
import {
  RulesPageFiltersDataState,
  selectorRulesPageFiltersData,
  SET_RULES_FILTERS_DATA_BY_KEY,
} from '../../../../../../../reducers/ipa/strategy/rulesPage/filters/data';
import {
  RESET_RULES_PAGE_FILTERS_QUERY,
  selectorRulesPageFiltersQuery,
  SET_RULES_PAGE_FILTERS_QUERY_BY_KEY,
} from '../../../../../../../reducers/ipa/strategy/rulesPage/filters/query';
import {
  RESET_RULES_PAGE_FILTERS_VALUE,
  RulesPageFiltersValueKey,
  RulesPageFiltersValueValue,
  selectorRulesPageFiltersValue,
  SET_RULES_PAGE_FILTERS_VALUE_BY_KEY,
} from '../../../../../../../reducers/ipa/strategy/rulesPage/filters/value';
import { selectorSetupIpaChannels } from '../../../../../../../reducers/reducerIPA/channels';
import { checkIfIsLoading } from '../../../../../../../utils';
import styles from './RulesPageFilters.module.scss';

type Key = keyof RulesPageFiltersDataState;

type RulesPageFiltersProps = RouteComponentProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const RulesPageFilters = ({
  query,
  data,
  value,
  cache,
  cattegories,
  channels,
  setDataByKey,
  setQueryByKey,
  setValueByKey,
  setCacheByKey,
  resetQuery,
  resetValue,
  resetCache,
}: RulesPageFiltersProps) => {
  const debounce = useDebounce();

  const handleSuccess = useCallback(
    (key: Key) => {
      return (data: DataItem[]) => {
        const newData = _.uniqBy([...(cache[key] ?? []), ...data], 'value');
        setDataByKey({ key, data: newData as DataItem[] });
      };
    },
    [cache, setDataByKey],
  );

  const handleSearch = useCallback(
    (key: Key) => {
      return (query: string) => {
        debounce(() => setQueryByKey({ key, query }));
      };
    },
    [debounce, setQueryByKey],
  );

  const handleClose = useCallback(
    (key: Key) => {
      return () => {
        setQueryByKey({ key, query: '' });
      };
    },
    [setQueryByKey],
  );

  const handleClean = useCallback(
    (key: Key) => {
      return () => {
        setQueryByKey({ key, query: '' });
        setValueByKey({ key, value: [] });
        setCacheByKey({ key, cache: [] });
      };
    },
    [setCacheByKey, setQueryByKey, setValueByKey],
  );

  const handleCleanAll = useCallback(() => {
    resetQuery();
    resetValue();
    resetCache();
  }, [resetCache, resetQuery, resetValue]);

  const handleChange = useCallback(
    (key: Key) => {
      return (value: string[]) => {
        setValueByKey({ key, value });
      };
    },
    [setValueByKey],
  );

  const handleSelectAllStores = useCallback(
    (key: Key) => {
      return (checked: boolean) => {
        let newData: string[] = [];

        if (checked) {
          newData = data[key]?.map(({ value }) => value) ?? [];
        }

        setValueByKey({ key, value: newData });
      };
    },
    [data, setValueByKey],
  );

  const { fetchStatus: loadingProducts } = useQuery({
    queryKey: ['ipa.filters.productIds', query.productIds],
    queryFn: () => service.listProducts(query?.productIds),
    onSuccess: handleSuccess('productIds'),
  });

  const { fetchStatus: loadingStoreIds } = useQuery({
    queryKey: ['ipa.filter.storeIds', query.searchStoreIds],
    queryFn: () => service.listStores(query.searchStoreIds),
    onSuccess: handleSuccess('searchStoreIds'),
  });

  const { fetchStatus: loadingClusters } = useQuery({
    queryKey: ['ipa.filter.clusters', query.clusters],
    queryFn: () => service.listClusters(query.clusters),
    onSuccess: handleSuccess('clusters'),
  });

  const { fetchStatus: loadingSensibilityType } = useQuery({
    queryKey: ['ipa.filter.segmentos'],
    queryFn: service.listaSegmentacao,
    onSuccess: handleSuccess('sensibilityType'),
  });

  useQuery({
    queryKey: ['ipa.filter.searchMechanismType'],
    queryFn: () => service.listMechanismType(),
    onSuccess: handleSuccess('searchMechanismType'),
  });

  useQuery({
    queryKey: ['ipa.filter.ruleStatus'],
    queryFn: () => service.listRuleStatus(),
    onSuccess: handleSuccess('ruleStatus'),
  });

  const { fetchStatus: loadingSearchAuthors } = useQuery({
    queryKey: ['ipa.filter.searchAuthors', query.searchAuthors],
    queryFn: () => service.listRuleCreators(query.searchAuthors),
    onSuccess: handleSuccess('searchAuthors'),
  });

  useQuery({
    queryKey: ['ipa.filter.rulePriceType', channels],
    queryFn: async () => await service.listRulePriceType(channels),
    onSuccess: handleSuccess('rulePriceType'),
  });

  const results = useQueries({
    queries: cattegories.map(({ value: key, level }) => ({
      queryKey: ['ipa.filter.segmentos', key, query[key]],
      queryFn: () => service.listSegment(query[key], level),
      onSuccess: handleSuccess(key),
    })),
  });

  const checkIfIsLoadingCattegory = useCallback((): boolean => {
    return results.some(({ fetchStatus }) => checkIfIsLoading(fetchStatus));
  }, [results]);

  return (
    <Layout.Section padded>
      <div className={styles.wrapper}>
        <div>
          <div className={styles.line}>
            <InnerFilter
              type="check"
              placeholder="Produtos"
              data={data?.productIds ?? []}
              value={value?.productIds ?? []}
              isLoading={checkIfIsLoading(loadingProducts)}
              onSearch={handleSearch('productIds')}
              onClose={handleClose('productIds')}
              onClean={handleClean('productIds')}
              onChange={handleChange('productIds')}
            />

            <InnerFilter
              type="check"
              placeholder="Loja"
              data={data?.searchStoreIds ?? []}
              value={value?.searchStoreIds ?? []}
              isLoading={checkIfIsLoading(loadingStoreIds)}
              onSearch={handleSearch('searchStoreIds')}
              onClose={handleClose('searchStoreIds')}
              onClean={handleClean('searchStoreIds')}
              onChange={handleChange('searchStoreIds')}
              selectAll={{
                onSelectAll: handleSelectAllStores('searchStoreIds'),
              }}
            />

            <InnerFilter
              type="check"
              placeholder="Cluster"
              data={data?.clusters ?? []}
              value={value?.clusters ?? []}
              isLoading={checkIfIsLoading(loadingClusters)}
              onSearch={handleSearch('clusters')}
              onClose={handleClose('clusters')}
              onClean={handleClean('clusters')}
              onChange={handleChange('clusters')}
            />

            <InnerFilter
              type="check"
              placeholder="Segmentação"
              data={data.sensibilityType ?? []}
              value={value.sensibilityType}
              isLoading={checkIfIsLoading(loadingSensibilityType)}
              onSearch={handleSearch('sensibilityType')}
              onChange={handleChange('sensibilityType')}
              onClose={handleClose('sensibilityType')}
              onClean={handleClean('sensibilityType')}
              groupBy="role"
              searchable={false}
            />

            <InnerFilter
              type="select"
              placeholder="Tipo de regra"
              data={data?.searchMechanismType ?? []}
              value={value?.searchMechanismType ?? []}
              onClean={handleClean('searchMechanismType')}
              onChange={handleChange('searchMechanismType')}
              searchable={false}
            />

            <InnerFilter
              type="select"
              placeholder="Status"
              data={data?.ruleStatus ?? []}
              value={value?.ruleStatus ?? []}
              onClean={handleClean('ruleStatus')}
              onChange={handleChange('ruleStatus')}
              searchable={false}
            />

            <InnerFilter
              type="check"
              placeholder="Responsável"
              data={data?.searchAuthors ?? []}
              value={value?.searchAuthors ?? []}
              isLoading={checkIfIsLoading(loadingSearchAuthors)}
              onSearch={handleSearch('searchAuthors')}
              onClose={handleClose('searchAuthors')}
              onClean={handleClean('searchAuthors')}
              onChange={handleChange('searchAuthors')}
            />

            <InnerFilter
              type="select"
              placeholder="Preço"
              data={data?.rulePriceType ?? []}
              value={value?.rulePriceType ?? []}
              onSearch={handleSearch('rulePriceType')}
              onClose={handleClose('rulePriceType')}
              onClean={handleClean('rulePriceType')}
              onChange={handleChange('rulePriceType')}
              searchable={false}
            />
          </div>
          <div className={styles.line}>
            {cattegories.map(({ value: key, level, name }) => (
              <InnerFilter
                key={key}
                type="check"
                data={data[key] ?? []}
                value={value[key]}
                onSearch={handleSearch(key)}
                onChange={handleChange(key)}
                onClose={handleClose(key)}
                onClean={handleClean(key)}
                isLoading={checkIfIsLoadingCattegory()}
                // @ts-expect-error Type 'Element' is not assignable to type 'string'
                placeholder={<SegmentsPlaceholder level={level} placeholder={name} />}
              />
            ))}
          </div>
        </div>
        <FilterButton type="button" onClick={handleCleanAll}>
          Limpar Filtros
        </FilterButton>
      </div>
    </Layout.Section>
  );
};

const mapStateToProps = (state: RootState) => ({
  query: selectorRulesPageFiltersQuery(state),
  data: selectorRulesPageFiltersData(state),
  value: selectorRulesPageFiltersValue(state),
  cache: selectorRulesPageFiltersCache(state),
  cattegories: selectorSegmentos(state),
  channels: selectorSetupIpaChannels(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setDataByKey: (payload: { key: Key; data: DataItem[] }) => dispatch(SET_RULES_FILTERS_DATA_BY_KEY(payload)),
  setQueryByKey: (payload: { key: Key; query: string }) => dispatch(SET_RULES_PAGE_FILTERS_QUERY_BY_KEY(payload)),
  setValueByKey: (payload: { key: RulesPageFiltersValueKey; value: RulesPageFiltersValueValue }) => dispatch(SET_RULES_PAGE_FILTERS_VALUE_BY_KEY(payload)),
  setCacheByKey: (payload: { key: Key; cache: DataItem[] }) => dispatch(SET_RULES_PAGE_FILTERS_CACHE_BY_KEY(payload)),
  resetValue: () => dispatch(RESET_RULES_PAGE_FILTERS_VALUE()),
  resetQuery: () => dispatch(RESET_RULES_PAGE_FILTERS_QUERY()),
  resetCache: () => dispatch(RESET_RULES_PAGE_FILTERS_CACHE()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(memo(RulesPageFilters)));
